import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import {
  PageHero,
  Reveal,
  usePageMeta,
} from "../components/Common";
import { INTEREST_OPTIONS, interestFromSlug } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full rounded-sm border border-navy/20 bg-white px-4 py-3 text-sm text-charcoal outline-none transition-colors duration-300 placeholder:text-charcoal/35 focus:border-crimson focus:ring-2 focus:ring-crimson/15";
const labelClass =
  "mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-navy/70";

const STEPS = [
  {
    title: "Send your enquiry",
    desc: "A few sentences about your situation is plenty to begin.",
  },
  {
    title: "I reply personally",
    desc: "Every enquiry is read and answered directly — no gatekeepers.",
  },
  {
    title: "We scope the work",
    desc: "A short conversation to see whether the fit is right for both of us.",
  },
];

const Contact = () => {
  usePageMeta(
    "Contact — English for Leaders",
    "Discuss a consulting project with English for Leaders. Every enquiry is read personally and answered directly.",
    "/contact"
  );

  const [searchParams] = useSearchParams();
  const prefill = interestFromSlug(searchParams.get("interest") || "");

  const [form, setForm] = useState({
    name: "",
    organisation: "",
    role: "",
    email: "",
    area_of_interest: prefill,
    message: "",
    website: "", // honeypot — must stay empty
  });
  const [status, setStatus] = useState("idle"); // idle | submitting | sent

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "submitting") return;
    setStatus("submitting");
    try {
      await axios.post(`${API}/enquiries`, form);
      setStatus("sent");
      toast.success("Thanks for getting in touch — your message is on its way.");
    } catch (err) {
      setStatus("idle");
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong sending your enquiry. Please try again."
      );
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        testid="contact-hero"
        eyebrow="Contact"
        title="Every Good Partnership Starts with a Conversation."
        intro="Have an idea, a challenge or a project in mind? I'd be pleased to hear about it. Whether you're exploring an early concept, seeking an independent perspective or looking for practical support, let's start with a conversation."
      />

      <section className="bg-white">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 py-16 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:py-24">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-display text-2xl tracking-tight text-navy sm:text-3xl">
                What happens next
              </h2>
              <ol className="mt-8 space-y-8">
                {STEPS.map((s, i) => (
                  <li key={s.title} className="flex gap-5">
                    <span
                      className="font-display text-3xl leading-none text-crimson/90"
                      aria-hidden="true"
                    >
                      0{i + 1}
                    </span>
                    <div>
                      <p className="text-base font-extrabold text-navy">{s.title}</p>
                      <p className="mt-1 text-sm leading-relaxed text-charcoal/70">
                        {s.desc}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
              <p className="mt-10 border-l-2 border-crimson pl-5 text-sm leading-relaxed text-charcoal/70">
                Prefer to keep it brief? A name, an email and one sentence about
                your project is enough to start.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              {status === "sent" ? (
                <div
                  className="flex h-full min-h-[420px] flex-col items-start justify-center border border-navy/10 bg-offwhite p-10"
                  data-testid="contact-success-panel"
                >
                  <CheckCircle2 className="h-10 w-10 text-crimson" strokeWidth={1.5} />
                  <h2 className="mt-6 font-display text-2xl tracking-tight text-navy sm:text-3xl">
                    Thanks for getting in touch. I look forward to reading your message.
                  </h2>
                  <p className="mt-4 max-w-md text-base leading-relaxed text-charcoal/75">
                    Every message is read personally, and I'll reply to you
                    directly at the email address you provided.
                  </p>
                  <button
                    type="button"
                    data-testid="contact-send-another-button"
                    onClick={() => {
                      setForm({
                        name: "",
                        organisation: "",
                        role: "",
                        email: "",
                        area_of_interest: "",
                        message: "",
                        website: "",
                      });
                      setStatus("idle");
                    }}
                    className="mt-8 text-sm font-bold text-crimson underline decoration-crimson/40 underline-offset-4 transition-colors hover:decoration-crimson"
                  >
                    Send another enquiry
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  data-testid="contact-form"
                  className="border border-navy/10 bg-offwhite p-8 sm:p-10"
                >
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="contact-name" className={labelClass}>
                        Name *
                      </label>
                      <input
                        id="contact-name"
                        data-testid="contact-name-input"
                        type="text"
                        required
                        maxLength={120}
                        value={form.name}
                        onChange={update("name")}
                        className={inputClass}
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-organisation" className={labelClass}>
                        Organisation
                      </label>
                      <input
                        id="contact-organisation"
                        data-testid="contact-organisation-input"
                        type="text"
                        maxLength={200}
                        value={form.organisation}
                        onChange={update("organisation")}
                        className={inputClass}
                        placeholder="School or organisation"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-role" className={labelClass}>
                        Role
                      </label>
                      <input
                        id="contact-role"
                        data-testid="contact-role-input"
                        type="text"
                        maxLength={120}
                        value={form.role}
                        onChange={update("role")}
                        className={inputClass}
                        placeholder="e.g. Principal, Director"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className={labelClass}>
                        Email *
                      </label>
                      <input
                        id="contact-email"
                        data-testid="contact-email-input"
                        type="email"
                        required
                        value={form.email}
                        onChange={update("email")}
                        className={inputClass}
                        placeholder="you@organisation.org"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="contact-interest" className={labelClass}>
                        Area of interest *
                      </label>
                      <select
                        id="contact-interest"
                        data-testid="contact-interest-select"
                        required
                        value={form.area_of_interest}
                        onChange={update("area_of_interest")}
                        className={inputClass}
                      >
                        <option value="" disabled>
                          Select an area of interest
                        </option>
                        {INTEREST_OPTIONS.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="contact-message" className={labelClass}>
                        Message *
                      </label>
                      <textarea
                        id="contact-message"
                        data-testid="contact-message-input"
                        required
                        maxLength={5000}
                        rows={6}
                        value={form.message}
                        onChange={update("message")}
                        className={`${inputClass} resize-y`}
                        placeholder="A few sentences about your school, organisation or project."
                      />
                    </div>
                  </div>

                  {/* Honeypot: hidden from humans, filled by bots */}
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={update("website")}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="hidden"
                  />

                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={status === "submitting"}
                    className="mt-8 inline-flex items-center gap-2.5 rounded-sm bg-crimson px-8 py-3.5 text-sm font-bold tracking-wide text-white transition-colors duration-300 hover:bg-crimson-dark disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {status === "submitting" && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                    {status === "submitting" ? "Sending…" : "Send My Message"}
                  </button>
                  <p className="mt-4 text-xs leading-relaxed text-charcoal/55">
                    Your details are used only to respond to your enquiry. See the{" "}
                    <a href="/privacy" className="underline underline-offset-2">
                      privacy note
                    </a>
                    .
                  </p>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
